import React, { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";
import { DateRange, dateRangeDisplayFormats } from "@smartsuite/types";
import type { DateRangeDisplayFormat } from "@smartsuite/types";
import { InputGroup, isEnterKey, isSpaceKey, Text } from "@smartsuite/react-ui/lib";
import { Icon } from "@smartsuite/react-ui/lib/components/utils/Icon";
import { DateRangeCalendar } from "@smartsuite/react-ui/lib/components/fields/Date/DateRange/DateRangeCalendar";
import { DateRangeDisplay } from "@smartsuite/react-ui/lib/components/fields/Date/DateRange/DateRangeDisplay";
import { SolutionColorVariables } from "@smartsuite/types/lib/constants/color";
import {
  calculateDateRangePercentProgress,
  dateRangeDaysDiff,
} from "@smartsuite/fields-logic/lib/utils/date-range";
import { getFormattedDateRange } from "@smartsuite/fields-logic/lib/formatters/date-range";

import { FormFieldProps } from "../../../types";
import { useFormField } from "../../../../hooks/useFormField";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { validateWithYup } from "../../../validator";
import { validatorText } from "../../../validator.text";
import { $t } from "../../../../utils/intl";
import { texts, dateRangeTexts } from "../date-range.text";
import {
  appendTimezoneToFormattedRange,
  createDateRangeSchema,
  defaultDateRangeValue,
  pinDateOnlyToUtcMidnight,
  toDateFnsDateFormat,
} from "../date-range.helpers";

import "../date-range.sass";
import { maxYearForSelect, minYearForSelect } from "../../date/DateFieldControl/DateFieldControl";
import { useFormContext } from "../../../../hooks/useFormContext";

const yearsRange = {
  from: minYearForSelect,
  to: maxYearForSelect(),
};

const getDaysCountText = (daysCount: number): string => {
  if (daysCount <= 0) return "";
  if (daysCount === 1) return $t(dateRangeTexts.oneDayText);
  return $t(dateRangeTexts.daysCountText, { count: daysCount });
};

const resolveDisplayFormat = (displayFormat?: string): DateRangeDisplayFormat =>
  dateRangeDisplayFormats.includes(displayFormat as DateRangeDisplayFormat)
    ? (displayFormat as DateRangeDisplayFormat)
    : "pill";

type DateRangeFieldControlProps = FormFieldProps;

export const DateRangeFieldControl: FC<DateRangeFieldControlProps> = ({
  name,
  label,
  context,
  caption,
  field,
  required,
  formItem,
  readOnly,
}) => {
  const { preferences } = useFormContext();
  const timeFormat = context.config.form_state.time_format ?? "24h";
  const timezone = preferences.timezone.value;
  // Memoized: an inline validate gets a fresh identity each render, and Formik's
  // useField keeps validateFn in its effect deps — the field would re-register
  // (and re-validate) on every render of the whole form.
  // A read-only field can't be filled by the user, so it must not enforce
  // required — otherwise a read-only required field blocks submit forever.
  const validate = useMemo(
    () =>
      validateWithYup(
        createDateRangeSchema(
          !!formItem.required && !readOnly,
          $t(validatorText.validationRequired)
        )
      ),
    [formItem.required, readOnly]
  );

  const formField = useFormField<DateRange | null>({ name, validate });

  const [isOpened, setIsOpened] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<number>();
  const outsidePointerDownRef = useRef(0);

  useEffect(() => () => window.clearTimeout(closeTimeoutRef.current), []);

  // Track pointer-downs outside both the trigger and the calendar dropdown, so
  // handleClose can tell a click-outside close (blur immediately, one click —
  // like due date) apart from a Done/Escape close (return focus to the trigger).
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      const inTrigger = !!triggerRef.current?.contains(target);
      const inCalendar =
        target instanceof Element && !!target.closest(".date-range-field-control__calendar");

      if (!inTrigger && !inCalendar) {
        outsidePointerDownRef.current = Date.now();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, []);

  const value = formField.value ?? defaultDateRangeValue;
  const hasValue = !!value.from_date?.date || !!value.to_date?.date;
  const displayFormat = resolveDisplayFormat(field?.params?.display_format);

  const { progressInPercents, progressInDays } = useMemo(() => {
    const daysInRange = dateRangeDaysDiff(value, timezone);

    return {
      progressInPercents: calculateDateRangePercentProgress(value, daysInRange, timezone),
      progressInDays: getDaysCountText(daysInRange),
    };
  }, [value, timezone]);
  // getFormattedDateRange reads the time format from preferences.locale.time_format, which
  // forms-web hardcodes to "12h"; force the form's configured time_format so the value's time
  // matches the picker and the other date fields (DateFieldControl does the same).
  const displayPreferences = useMemo(
    () => ({ ...preferences, locale: { ...preferences.locale, time_format: timeFormat } }),
    [preferences, timeFormat]
  );

  // DateRangeCalendar formats its inputs with locale.date_format, and only converts the doubled
  // moment tokens — a single-letter day token (e.g. "D-M-yyyy") reaches date-fns as day-of-year and
  // throws, taking the picker down (BUG: Date Range crashes for some locales). Normalize first.
  const calendarPreferences = useMemo(
    () => ({
      ...preferences,
      locale: {
        ...preferences.locale,
        date_format: toDateFnsDateFormat(preferences.locale.date_format),
      },
    }),
    [preferences]
  );

  // getFormattedDateRange formats the dates per the field's display_format (compact/standard/long/pill);
  // DateRangeDisplay/DateRangeArrowValue render date strings verbatim, so format here (day-count/progress use the raw value above).
  // pinDateOnlyToUtcMidnight keeps the shown day aligned with the picker across timezones (see helper).
  const formattedValue = useMemo(
    () =>
      appendTimezoneToFormattedRange(
        getFormattedDateRange(pinDateOnlyToUtcMidnight(value), displayPreferences, displayFormat),
        value,
        timezone
      ),
    [value, displayPreferences, displayFormat, timezone]
  );

  // The picker resolves a date-only boundary with dateValueToDate, which shifts a
  // bare "YYYY-MM-DD" (prefill/default) by the browser offset and highlights the
  // wrong day. Pin date-only boundaries to UTC midnight — matching the display
  // path above — so the picker opens on the intended day in every timezone.
  const pickerValue = useMemo(
    () => (formField.value ? pinDateOnlyToUtcMidnight(formField.value) : null),
    [formField.value]
  );

  const handleOpen = useCallback(() => {
    if (readOnly) return;
    setIsOpened(true);
    formField.onFocus();
  }, [readOnly, formField]);

  // The trigger's DOM focus drives the InputGroup active state, exactly like the
  // input of every other field — so plain Tab focus (or the focus returned after
  // closing the picker) shows the same focused UI.
  const handleTriggerFocus = useCallback(() => {
    if (readOnly) return;
    formField.onFocus();
  }, [readOnly, formField]);

  const handleTriggerBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      // Focus moving into the calendar dropdown (portal) or onto the clear
      // button inside the trigger is not a real blur.
      if (isOpened) return;
      if (event.currentTarget.contains(event.relatedTarget as Node)) return;
      formField.onBlur();
    },
    [isOpened, formField]
  );

  const handleClear = useCallback(
    (event?: MouseEvent) => {
      event?.stopPropagation();
      // Close the picker too: clearing while it is open would race its click-outside
      // save (a native listener that synthetic stopPropagation cannot stop).
      setIsOpened(false);
      formField.onChange(defaultDateRangeValue);
    },
    [formField]
  );

  // Clicking the clear button must only clear the value — prevent it from taking
  // focus (which would bubble to the trigger and activate the field); focus is
  // the default action of mousedown, and the click still fires. Keyboard focus
  // on the button works as usual.
  const handleClearMouseDown = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  const handleClose = useCallback(() => {
    setIsOpened(false);
    const closedByOutsideClick = Date.now() - outsidePointerDownRef.current < 200;

    // Deferred (with unmount cleanup): the just-selected value commits first — a blur
    // in the same tick as the calendar's onChange would resurrect the "required"
    // error from the stale value. A close caused by a click outside blurs right away
    // (one click fully leaves the field, like due date); a Done/Escape close returns
    // focus to the trigger instead, keeping the keyboard flow.
    window.clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = window.setTimeout(() => {
      if (!closedByOutsideClick && document.activeElement === document.body) {
        triggerRef.current?.focus();
      }
      if (document.activeElement !== triggerRef.current) {
        formField.onBlur();
      }
    }, 0);
  }, [formField]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (isEnterKey(event) || isSpaceKey(event)) {
        event.preventDefault();
        handleOpen();
      }
    },
    [handleOpen]
  );

  const classes = cx("date-range-field-control", {
    "is-empty": !hasValue,
  });

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      label={label}
      state={formField.state}
      caption={caption}
      required={required}
      helpText={formItem.params.helpText}
      errorMessage={formField.errorMessage}
      readOnly={readOnly}
    >
      <InputGroup className={classes} state={formField.state} isReadOnly={readOnly}>
        <div
          ref={triggerRef}
          className={cx("date-range-field-control__trigger", [
            `date-range-field-control__trigger--${displayFormat}`,
          ])}
          role="button"
          tabIndex={readOnly ? -1 : 0}
          aria-label={$t(dateRangeTexts.triggerAriaLabel)}
          aria-haspopup="dialog"
          aria-expanded={isOpened}
          onClick={handleOpen}
          onKeyDown={handleKeyDown}
          onFocus={handleTriggerFocus}
          onBlur={handleTriggerBlur}
        >
          {hasValue ? (
            <DateRangeDisplay
              value={formattedValue}
              type={displayFormat}
              color={SolutionColorVariables.sc1}
              progressInPercents={progressInPercents}
              progressInDays={progressInDays}
              isFilled={displayFormat === "pill"}
              className="date-range-field-control__value"
            />
          ) : (
            <Text className="date-range-field-control__placeholder" color="secondary">
              {$t(dateRangeTexts.placeholder)}
            </Text>
          )}

          {hasValue && !readOnly && (
            // Mirrors DueDateControl's clear: a plain button revealed on hover
            // (see date-range.sass), not an always-visible IconButton.
            <button
              type="button"
              className="date-range-field-control__clear"
              aria-label={$t(dateRangeTexts.removeDateAriaLabel)}
              onMouseDown={handleClearMouseDown}
              onClick={handleClear}
            >
              <Icon name="close" size="M" color="currentColor" transition />
            </button>
          )}
        </div>
      </InputGroup>

      <DateRangeCalendar
        isOpened={isOpened}
        targetElementRef={triggerRef}
        value={pickerValue}
        texts={texts}
        timezone={timezone}
        timeFormat={timeFormat}
        userPreferences={calendarPreferences}
        yearsOptionStart={yearsRange.from}
        yearsOptionEnd={yearsRange.to}
        className="date-range-field-control__calendar"
        // react-ui derives include_time correctly (from the picked time), so trust
        // the emitted value instead of re-deriving the flag from the date string.
        onChange={(range) => formField.onChange(range)}
        onClose={handleClose}
      />
    </FormFieldControl>
  );
};
